import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, Briefcase, StickyNote, ClipboardCheck, Upload, FileText, Pencil, Save, X, GraduationCap, Plus, Trash2, FileUp, Loader2, Brain, ClipboardList, UsersRound, Settings, TrendingUp, LogIn, Receipt, Tag, Copy, CheckCircle2, Lock, Unlock, KeyRound, ShieldAlert, Bot } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ClientTimelineTab } from "./clientDetail/ClientTimelineTab";
import { ClientAccessTab } from "./clientDetail/ClientAccessTab";
import { NoteDialog, AuditDialog, EnrollDialog, DeleteConfirmDialog, AssignStaffDialog, AddServiceDialog, FormViewDialog, AssignAccDialog, TagsDialog, InvoiceDetailDialog } from "./clientDetail/ClientDetailDialogs";
import ClientCrmMetaPanel from "../components/ClientCrmMetaPanel";
import ClientCrmOpportunities from "../components/ClientCrmOpportunities";
import PageHeader from "../components/PageHeader";

export default function AdminClientDetail() {
  const ROLE_LABELS = { admin: "Administrateur", coach: "Coach", sous_traitant: "Sous-traitant", comptable: "Comptable", client: "Client" };
  const ROLE_COLORS = { admin: "#f7c935", coach: "#3b82f6", sous_traitant: "#10b981", comptable: "#f59e0b", client: "#8b5cf6" };
  const { userId } = useParams();
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();

  const handleImpersonate = async () => {
    if (!window.confirm("Vous allez vous connecter en tant que ce client. Continuez ?")) return;
    try {
      // Save admin token to return later
      const adminToken = localStorage.getItem("blitzz_token");
      localStorage.setItem("blitzz_admin_token", adminToken);
      const r = await api.post(`/admin/impersonate/${userId}`);
      localStorage.setItem("blitzz_token", r.data.token);
      window.location.href = "/dashboard";
    } catch (e) {
      alert("Erreur: " + (e.response?.data?.detail || e.message));
    }
  };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({});

  // Audit edit
  const [editingAudit, setEditingAudit] = useState(null);
  const [auditForm, setAuditForm] = useState({});

  // New note dialog
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteForm, setNoteForm] = useState({ title: "", content: "", category: "general" });
  const [editingNote, setEditingNote] = useState(null);

  // New audit dialog
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [newAuditForm, setNewAuditForm] = useState({ title: "", content: "", status: "en_cours", recommendations: "" });

  // Doc upload
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Enroll
  const [showEnroll, setShowEnroll] = useState(false);
  const [enrollFormationId, setEnrollFormationId] = useState("");

  // Delete
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // AI summary
  const [generatingAI, setGeneratingAI] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);

  // Assignments
  const [assignments, setAssignments] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [showAssignStaff, setShowAssignStaff] = useState(false);
  const [assignStaffId, setAssignStaffId] = useState("");

  // Client access settings
  const [clientAccess, setClientAccess] = useState({
    messages: true, agenda: false,
    audits: false, accompagnement: true, services: true,
    formations: false, facturation: false, documents: false, club: false,
    social: false,
    documents_gestion: false, fiche: true,
  });
  const [savingAccess, setSavingAccess] = useState(false);
  const [resettingPw, setResettingPw] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showResetPw, setShowResetPw] = useState(false);
  const [blockingAccount, setBlockingAccount] = useState(false);

  // Services overview
  const [svcOverview, setSvcOverview] = useState(null);

  // Add service from Builder catalog
  const [showAddService, setShowAddService] = useState(false);
  const [catalogServices, setCatalogServices] = useState([]);
  const [docTemplates, setDocTemplates] = useState([]);
  const [selectedCatalog, setSelectedCatalog] = useState("");
  const [selectedIntakeTemplate, setSelectedIntakeTemplate] = useState("");
  const [intakeForms, setIntakeForms] = useState([]);
  const [viewingForm, setViewingForm] = useState(null);
  const [showFormView, setShowFormView] = useState(false);

  // Assign accompaniment from templates
  const [showAssignAcc, setShowAssignAcc] = useState(false);
  const [accTemplates, setAccTemplates] = useState([]);
  const [selectedAccTemplate, setSelectedAccTemplate] = useState("");

  // Import fiche from file
  const [importingFiche, setImportingFiche] = useState(false);

  // Client invoices
  const [clientInvoices, setClientInvoices] = useState([]);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [invoicePayments, setInvoicePayments] = useState([]);

  // Client timeline/parcours
  const [clientTimeline, setClientTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [timelineFilter, setTimelineFilter] = useState("all");

  // Health score
  const [healthScore, setHealthScore] = useState(null);

  // Tags / Balises
  const [tags, setTags] = useState({});
  const [showTags, setShowTags] = useState(false);
  const [copiedTag, setCopiedTag] = useState(null);

  const loadData = async () => {
    try {
      const r = await api.get(`/admin/clients/${userId}`);
      setData(r.data);
      setProfileForm(r.data.profile || {});
      if (r.data.fiche?.ai_summary) setAiSummary(r.data.fiche.ai_summary);
      // Load assignments
      const asgn = await api.get(`/admin/assignments/${userId}`);
      setAssignments(asgn.data);
      // Load staff for assignment
      try { const s = await api.get("/admin/staff"); setAllStaff(s.data); } catch {}
      // Load client access settings
      try { const ac = await api.get(`/admin/clients/${userId}/access`); setClientAccess(ac.data); } catch {}
      // Load services overview
      try { const ov = await api.get(`/admin/clients/${userId}/services-overview`); setSvcOverview(ov.data); } catch {}
      // Load Builder catalog services & doc templates & acc templates
      try { const cs = await api.get("/catalog/services"); setCatalogServices(cs.data || []); } catch {}
      try { const dt = await api.get("/catalog/document-templates"); setDocTemplates(dt.data || []); } catch {}
      try { const at = await api.get("/accompaniment-templates"); setAccTemplates(at.data || []); } catch {}
      // Load intake forms for this client
      try { const iforms = await api.get(`/intake-forms?client_id=${userId}`); setIntakeForms(iforms.data || []); } catch {}
      // Load client invoices
      try { const inv = await api.get(`/admin/clients/${userId}/invoices`); setClientInvoices(inv.data || []); } catch {}
      // Load tags
      try { const t = await api.get(`/admin/clients/${userId}/tags`); setTags(t.data || {}); } catch {}
      // Load timeline
      try { const tl = await api.get(`/admin/clients/${userId}/timeline`); setClientTimeline(tl.data || []); } catch {}
      // Load health score
      try { const hs = await api.get(`/admin/clients/${userId}/health-score`); setHealthScore(hs.data); } catch {}
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadData(); }, [userId]);

  const saveProfile = async () => {
    await api.put(`/admin/clients/${userId}/profile`, profileForm);
    setEditing(false);
    loadData();
  };

  // Notes CRUD
  const saveNote = async () => {
    if (editingNote) {
      await api.put(`/notes/${editingNote}`, { ...noteForm, client_id: userId });
    } else {
      await api.post("/notes", { ...noteForm, client_id: userId });
    }
    setShowNoteDialog(false);
    setEditingNote(null);
    setNoteForm({ title: "", content: "", category: "general" });
    loadData();
  };

  const deleteNote = async (noteId) => {
    await api.delete(`/notes/${noteId}`);
    loadData();
  };

  const openEditNote = (note) => {
    setEditingNote(note.note_id);
    setNoteForm({ title: note.title, content: note.content, category: note.category || "general" });
    setShowNoteDialog(true);
  };

  // Audits CRUD
  const saveAudit = async () => {
    const payload = {
      title: newAuditForm.title,
      content: newAuditForm.content,
      status: newAuditForm.status,
      recommendations: newAuditForm.recommendations ? newAuditForm.recommendations.split("\n").filter(Boolean) : []
    };
    if (editingAudit) {
      await api.put(`/audits/${editingAudit}`, payload);
    } else {
      await api.post("/audits", { ...payload, client_id: userId });
    }
    setShowAuditDialog(false);
    setEditingAudit(null);
    setNewAuditForm({ title: "", content: "", status: "en_cours", recommendations: "" });
    loadData();
  };

  const deleteAudit = async (auditId) => {
    await api.delete(`/audits/${auditId}`);
    loadData();
  };

  const openEditAudit = (audit) => {
    setEditingAudit(audit.audit_id);
    setNewAuditForm({
      title: audit.title,
      content: audit.content,
      status: audit.status,
      recommendations: (audit.recommendations || []).join("\n")
    });
    setShowAuditDialog(true);
  };

  // Doc upload
  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/documents/upload?client_id=${userId}&title=${encodeURIComponent(file.name)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      loadData();
    } catch (err) { console.error(err); }
    setUploadingDoc(false);
    e.target.value = "";
  };

  // Enroll client in formation
  const handleEnroll = async () => {
    if (!enrollFormationId) return;
    await api.post("/formations/enroll", { client_id: userId, formation_id: enrollFormationId });
    setShowEnroll(false);
    setEnrollFormationId("");
    loadData();
  };

  // Delete client
  const handleDeleteClient = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/clients/${userId}`);
      navigate("/admin/clients");
    } catch (e) { console.error(e); }
    setDeleting(false);
  };

  // Generate AI Summary
  const handleGenerateAI = async () => {
    setGeneratingAI(true);
    try {
      const r = await api.post(`/fiche-renseignement/${userId}/ai-summary`);
      setAiSummary(r.data.summary);
    } catch (e) { console.error(e); }
    setGeneratingAI(false);
  };

  // Assign staff
  const handleAssignStaff = async () => {
    if (!assignStaffId) return;
    try {
      await api.post("/admin/assignments", { client_id: userId, staff_id: assignStaffId });
      setShowAssignStaff(false);
      setAssignStaffId("");
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    await api.delete(`/admin/assignments/${assignmentId}`);
    loadData();
  };

  const saveClientAccess = async () => {
    setSavingAccess(true);
    try {
      await api.put(`/admin/clients/${userId}/access`, clientAccess);
    } catch (e) { console.error(e); }
    setSavingAccess(false);
  };

  const toggleAccess = (key) => {
    const updated = { ...clientAccess, [key]: !clientAccess[key] };
    setClientAccess(updated);
  };

  const handleToggleBlock = async () => {
    setBlockingAccount(true);
    try {
      const r = await api.post(`/admin/clients/${userId}/toggle-block`);
      loadData();
      alert(r.data.message);
    } catch (e) { alert("Erreur: " + (e.response?.data?.detail || e.message)); }
    setBlockingAccount(false);
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6) { alert("Mot de passe minimum 6 caracteres"); return; }
    setResettingPw(true);
    try {
      await api.post(`/admin/clients/${userId}/reset-password`, { password: newPassword });
      setNewPassword("");
      setShowResetPw(false);
      alert("Mot de passe reinitialise avec succes");
    } catch (e) { alert("Erreur: " + (e.response?.data?.detail || e.message)); }
    setResettingPw(false);
  };

  const handleAddServiceFromCatalog = async () => {
    if (!selectedCatalog) return;
    const cat = catalogServices.find(c => c.catalog_id === selectedCatalog);
    if (!cat) return;
    try {
      await api.post("/services", {
        client_id: userId,
        name: cat.name,
        description: cat.description || "",
        category: cat.category || "",
        price: cat.price || cat.base_price || "",
        catalog_service_id: cat.catalog_id,
        intake_form_template_id: selectedIntakeTemplate || "",
      });
      setShowAddService(false);
      setSelectedCatalog("");
      setSelectedIntakeTemplate("");
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Supprimer ce service ?")) return;
    try { await api.delete(`/services/${serviceId}`); loadData(); } catch (e) { console.error(e); }
  };

  const deleteAccompagnement = async (accId) => {
    if (!window.confirm("Supprimer cet accompagnement ? Cette action est irreversible.")) return;
    try { await api.delete(`/accompaniments/${accId}`); loadData(); } catch (e) { console.error(e); }
  };


  const handleAssignAccompaniment = async () => {
    if (!selectedAccTemplate) return;
    const tpl = accTemplates.find(t => t.template_id === selectedAccTemplate);
    if (!tpl) return;
    try {
      await api.post("/accompaniments", {
        client_id: userId,
        title: tpl.title || tpl.name || "Accompagnement",
        description: tpl.description || "",
        formula: tpl.formula || "START",
        price: tpl.price || "",
        duration_months: tpl.duration_months || 0,
        phases: tpl.phases || [],
        template_id: tpl.template_id,
      });
      setShowAssignAcc(false);
      setSelectedAccTemplate("");
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleImportFiche = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportingFiche(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/fiche-renseignement/${userId}/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      alert(`Import reussi : ${r.data.filled_fields} champs remplis`);
      loadData();
    } catch (e) { alert("Erreur import : " + (e.response?.data?.detail || "")); }
    setImportingFiche(false);
    e.target.value = "";
  };

  if (loading) return <div className="p-8 text-slate-400">Chargement...</div>;
  if (!data) return <div className="p-8 text-red-400">Client non trouve</div>;

  const { user, profile, services, notes, audits, accompaniments, fec_files, documents, enrollments = [], formations = [], fiche = {} } = data;

  const enrolledFormationIds = enrollments.map(e => e.formation_id);
  const availableFormations = formations.filter(f => !enrolledFormationIds.includes(f.formation_id));

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-client-detail">
      <PageHeader
        icon={(
          <div className="w-full h-full rounded-xl flex items-center justify-center text-base font-black" style={{ background: "#461d8a", color: "#f7c935" }}>
            {user?.name?.[0]?.toUpperCase() || "C"}
          </div>
        )}
        title={user?.name || "Client"}
        description={`${user?.email || ""}${profile?.company_name ? ` · ${profile.company_name}` : ""}`}
        breadcrumb={[{ label: "Admin" }, { label: "Clients" }, { label: user?.name || "Détail" }]}
        testId="admin-client-detail-header"
        actions={(
          <>
            <Button onClick={() => navigate("/admin/clients")} variant="ghost" className="text-slate-400 hover:bg-white/5" data-testid="back-to-clients">
              <ArrowLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
            <Button onClick={() => setEditing(!editing)} variant="outline" className="border-white/10 text-white hover:bg-white/5">
              {editing ? <><X className="w-4 h-4 mr-2" /> Annuler</> : <><Pencil className="w-4 h-4 mr-2" /> Modifier</>}
            </Button>
            <Button onClick={handleImpersonate} variant="outline" className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10" data-testid="impersonate-btn">
              <LogIn className="w-4 h-4 mr-2" /> Se connecter en tant que
            </Button>
            <Button onClick={() => setShowDeleteConfirm(true)} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="delete-client-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          </>
        )}
      />

      <Tabs defaultValue="profil">
        <TabsList className="bg-[#14101c] border border-white/10 mb-6 flex-wrap">
          <TabsTrigger value="profil" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c]">Profil</TabsTrigger>
          <TabsTrigger value="facturation" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c]" data-testid="tab-facturation"><Receipt className="w-3.5 h-3.5 mr-1 inline" />Facturation</TabsTrigger>
          <TabsTrigger value="fiche" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c]">Fiche</TabsTrigger>
          <TabsTrigger value="prestations" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c]" data-testid="tab-prestations">Prestations ({accompaniments.length + services.length})</TabsTrigger>
          <TabsTrigger value="formations" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c]">Formations ({enrollments.length})</TabsTrigger>
          <TabsTrigger value="notes" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c]">Notes ({notes.length})</TabsTrigger>
          <TabsTrigger value="audits" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c]">Audits ({audits.length})</TabsTrigger>
          <TabsTrigger value="crm" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c]" data-testid="tab-crm"><Bot className="w-3.5 h-3.5 mr-1 inline" />CRM &amp; IA</TabsTrigger>
          <TabsTrigger value="equipe" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c]">Equipe ({assignments.length})</TabsTrigger>
          <TabsTrigger value="documents" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c]">Documents ({documents.length})</TabsTrigger>
          <TabsTrigger value="timeline" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c]" data-testid="tab-timeline"><TrendingUp className="w-3.5 h-3.5 mr-1 inline" />Parcours</TabsTrigger>
          <TabsTrigger value="acces" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c]" data-testid="tab-acces"><Settings className="w-3.5 h-3.5 mr-1 inline" />Acces</TabsTrigger>
        </TabsList>

        {/* Profil Tab */}
        <TabsContent value="profil">
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-[#14101c] border border-white/10 rounded-xl p-6">
            {editing ? (
              <div className="space-y-6">
                <h3 className="text-lg font-bold" style={{ color: "#f7c935" }}>Informations generales</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { key: "company_name", label: "Raison sociale" },
                    { key: "legal_form", label: "Forme juridique" },
                    { key: "siret", label: "SIRET" },
                    { key: "siren", label: "SIREN" },
                    { key: "tva_intra", label: "N TVA Intracommunautaire" },
                    { key: "ape_code", label: "Code APE" },
                    { key: "rcs", label: "RCS" },
                    { key: "capital_social", label: "Capital social" },
                    { key: "address", label: "Adresse" },
                    { key: "postal_code", label: "Code postal" },
                    { key: "city", label: "Ville" },
                    { key: "phone", label: "Telephone" },
                    { key: "website", label: "Site web" },
                    { key: "sector", label: "Secteur" },
                    { key: "employee_count", label: "Effectif" },
                  ].map(({ key, label }) => (
                    <div key={key}>
                      <label className="text-xs text-slate-500 mb-1 block">{label}</label>
                      <Input value={profileForm[key] || ""} onChange={(e) => setProfileForm({ ...profileForm, [key]: e.target.value })} className="bg-[#0b0811] border-white/10 text-white h-9 text-sm focus:border-[#f7c935]" data-testid={`profile-${key}`} />
                    </div>
                  ))}
                  <div className="md:col-span-2">
                    <label className="text-xs text-slate-500 mb-1 block">Description activite</label>
                    <Textarea value={profileForm.activity_description || ""} onChange={(e) => setProfileForm({ ...profileForm, activity_description: e.target.value })} className="bg-[#0b0811] border-white/10 text-white text-sm focus:border-[#f7c935]" rows={3} />
                  </div>
                </div>

                <h3 className="text-lg font-bold" style={{ color: "#f7c935" }}>Informations de facturation</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { key: "billing_email", label: "Email de facturation" },
                    { key: "iban", label: "IBAN" },
                    { key: "bic", label: "BIC / SWIFT" },
                    { key: "payment_terms", label: "Conditions de paiement" },
                  ].map(({ key, label }) => (
                    <div key={key}>
                      <label className="text-xs text-slate-500 mb-1 block">{label}</label>
                      <Input value={profileForm[key] || ""} onChange={(e) => setProfileForm({ ...profileForm, [key]: e.target.value })} className="bg-[#0b0811] border-white/10 text-white h-9 text-sm focus:border-[#f7c935]" data-testid={`profile-${key}`} />
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-3">
                  <Button onClick={saveProfile} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold">
                    <Save className="w-4 h-4 mr-2" /> Enregistrer
                  </Button>
                  <Button onClick={() => setShowTags(true)} variant="outline" className="border-white/10 text-white hover:bg-white/5">
                    <Tag className="w-4 h-4 mr-2" /> Voir les balises
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold" style={{ color: "#f7c935" }}>Fiche client</h3>
                  <Button onClick={() => setShowTags(true)} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs" data-testid="show-tags-btn">
                    <Tag className="w-3.5 h-3.5 mr-1" /> Balises
                  </Button>
                </div>
                {!profile?.company_name ? (
                  <p className="text-slate-500">Aucune fiche renseignee. Cliquez sur "Modifier" pour creer la fiche.</p>
                ) : (
                  <div className="space-y-6">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Informations generales</div>
                      <div className="grid md:grid-cols-2 gap-3">
                        {[
                          { label: "Raison sociale", value: profile.company_name },
                          { label: "Forme juridique", value: profile.legal_form },
                          { label: "SIRET", value: profile.siret },
                          { label: "SIREN", value: profile.siren },
                          { label: "TVA Intra.", value: profile.tva_intra },
                          { label: "Code APE", value: profile.ape_code },
                          { label: "RCS", value: profile.rcs },
                          { label: "Capital social", value: profile.capital_social },
                          { label: "Adresse", value: `${profile.address || ""} ${profile.postal_code || ""} ${profile.city || ""}` },
                          { label: "Telephone", value: profile.phone },
                          { label: "Site web", value: profile.website },
                          { label: "Secteur", value: profile.sector },
                          { label: "Effectif", value: profile.employee_count },
                        ].map(({ label, value }) => value && value.trim() && (
                          <div key={label} className="py-2 border-b border-white/5">
                            <div className="text-xs text-slate-500">{label}</div>
                            <div className="text-sm font-medium">{value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {(profile.billing_email || profile.iban || profile.bic || profile.payment_terms) && (
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Facturation</div>
                        <div className="grid md:grid-cols-2 gap-3">
                          {[
                            { label: "Email facturation", value: profile.billing_email },
                            { label: "IBAN", value: profile.iban },
                            { label: "BIC / SWIFT", value: profile.bic },
                            { label: "Conditions de paiement", value: profile.payment_terms },
                          ].map(({ label, value }) => value && value.trim() && (
                            <div key={label} className="py-2 border-b border-white/5">
                              <div className="text-xs text-slate-500">{label}</div>
                              <div className="text-sm font-medium">{value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Score de sante client */}
            {healthScore && (
              <div className="mt-6 bg-[#14101c] border border-white/5 rounded-xl p-5" data-testid="health-score-widget">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Score de sante</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                    healthScore.level === "excellent" ? "bg-green-500/10 text-green-400" :
                    healthScore.level === "bon" ? "bg-blue-500/10 text-blue-400" :
                    healthScore.level === "moyen" ? "bg-yellow-500/10 text-yellow-400" :
                    healthScore.level === "faible" ? "bg-orange-500/10 text-orange-400" :
                    "bg-red-500/10 text-red-400"
                  }`}>{healthScore.level}</span>
                </div>
                <div className="flex items-center gap-4 mb-3">
                  <div className="relative w-20 h-20 flex-shrink-0">
                    <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                      <circle cx="40" cy="40" r="34" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
                      <circle cx="40" cy="40" r="34" fill="none"
                        stroke={healthScore.score >= 80 ? "#22c55e" : healthScore.score >= 60 ? "#3b82f6" : healthScore.score >= 40 ? "#eab308" : "#ef4444"}
                        strokeWidth="8" strokeLinecap="round"
                        strokeDasharray={`${(healthScore.score / 100) * 213.6} 213.6`} />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-lg font-black" style={{ color: healthScore.score >= 80 ? "#22c55e" : healthScore.score >= 60 ? "#3b82f6" : healthScore.score >= 40 ? "#eab308" : "#ef4444" }}>{healthScore.score}</span>
                    </div>
                  </div>
                  <div className="flex-1 space-y-2">
                    {healthScore.details?.map((d, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-500 w-20 flex-shrink-0">{d.category}</span>
                        <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${(d.score / d.max) * 100}%`, background: d.score >= d.max * 0.7 ? "#22c55e" : d.score >= d.max * 0.4 ? "#eab308" : "#ef4444" }} />
                        </div>
                        <span className="text-[10px] text-slate-600 w-8 text-right">{d.score}/{d.max}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Prestations actives (services + accompagnements) */}
            <div className="mt-6">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Prestations actives</div>
              {services.length === 0 && accompaniments.length === 0 ? (
                <p className="text-sm text-slate-500 p-4 bg-[#14101c] border border-white/10 rounded-xl text-center">Aucune prestation active</p>
              ) : (
                <div className="grid md:grid-cols-2 gap-3">
                  {accompaniments.filter(a => a.status !== "termine").map(acc => {
                    const total = acc.phases?.reduce((s, p) => s + (p.tasks?.length || 0), 0) || 0;
                    const done = acc.phases?.reduce((s, p) => s + (p.tasks?.filter(t => t.status === "done").length || 0), 0) || 0;
                    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                    return (
                      <div key={acc.accompaniment_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-[#f7c935]/30 transition-colors cursor-pointer"
                        onClick={() => navigate(`/admin/accompagnements/${acc.accompaniment_id}`)} data-testid={`prestation-acc-${acc.accompaniment_id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <ClipboardCheck className="w-4 h-4 text-purple-400" />
                            <span className="text-sm font-medium">{acc.title}</span>
                          </div>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/10 text-purple-400">Accompagnement</span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>{acc.formula}{acc.price ? ` - ${acc.price} EUR` : ""}</span>
                          <span className="font-bold" style={{ color: "#f7c935" }}>{pct}%</span>
                        </div>
                        {total > 0 && (
                          <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
                            <div className="h-full rounded-full bg-[#f7c935] transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {services.map(srv => (
                    <div key={srv.service_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-white/15 transition-colors" data-testid={`prestation-srv-${srv.service_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-blue-400" />
                          <span className="text-sm font-medium">{srv.name}</span>
                        </div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${srv.status === "active" ? "bg-green-500/10 text-green-400" : "bg-slate-500/10 text-slate-400"}`}>
                          Service
                        </span>
                      </div>
                      <div className="text-xs text-slate-500">
                        {srv.category && <span className="mr-3">{srv.category}</span>}
                        {srv.price && <span className="text-[#f7c935] font-bold">{srv.price} EUR</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Personnes en charge - moved to Profil */}
            <div className="mt-6">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Personnes en charge</div>
              {(!svcOverview?.assigned_staff || svcOverview.assigned_staff.length === 0) ? (
                <p className="text-sm text-slate-500 p-4 bg-[#14101c] border border-white/10 rounded-xl text-center">Aucun membre attribue</p>
              ) : (
                <div className="grid md:grid-cols-2 gap-3">
                  {svcOverview.assigned_staff.map(s => (
                    <div key={s.user_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 flex items-center gap-3 hover:border-white/15 transition-colors">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                        style={{ background: "#461d8a", color: ROLE_COLORS[s.role] || "#f7c935" }}>
                        {s.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <div className="text-sm font-medium">{s.name}</div>
                        <div className="text-xs text-slate-500">{ROLE_LABELS[s.role] || s.role}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </div>
            <aside className="space-y-4" data-testid="client-profile-sidebar">
              <ClientCrmMetaPanel api={api} userId={userId} client={user} onChanged={() => loadData()} />
            </aside>
          </div>
        </TabsContent>

        {/* Facturation Tab - moved after Profil */}
        <TabsContent value="facturation">
          <div className="space-y-4" data-testid="client-facturation-content">
            {/* Revenue summary */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
                <div className="text-[10px] text-slate-500 uppercase">CA Total</div>
                <div className="text-xl font-black text-[#f7c935]">{(svcOverview?.total_ca || 0).toLocaleString("fr")} EUR</div>
              </div>
              <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
                <div className="text-[10px] text-slate-500 uppercase">Factures</div>
                <div className="text-xl font-black text-blue-400">{clientInvoices.filter(i => i.type === "facture").length}</div>
              </div>
              <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
                <div className="text-[10px] text-slate-500 uppercase">Devis</div>
                <div className="text-xl font-black text-green-400">{clientInvoices.filter(i => i.type === "devis").length}</div>
              </div>
              <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
                <div className="text-[10px] text-slate-500 uppercase">Avoirs</div>
                <div className="text-xl font-black text-red-400">{clientInvoices.filter(i => i.type === "avoir").length}</div>
              </div>
            </div>
            {/* Invoice list */}
            {clientInvoices.length === 0 ? (
              <div className="bg-[#14101c] border border-white/10 rounded-xl p-8 text-center text-slate-500">Aucune facture pour ce client</div>
            ) : (
              <div className="space-y-2">
                {clientInvoices.map(inv => {
                  const sMap = { brouillon: { l: "Brouillon", c: "bg-yellow-500/10 text-yellow-400" }, envoye: { l: "Envoyee", c: "bg-blue-500/10 text-blue-400" }, signe: { l: "Signee", c: "bg-purple-500/10 text-purple-400" }, paye: { l: "Payee", c: "bg-green-500/10 text-green-400" }, impaye: { l: "Impayee", c: "bg-orange-500/10 text-orange-400" }, en_retard: { l: "En retard", c: "bg-red-500/10 text-red-400" }, annule: { l: "Annulee", c: "bg-slate-500/10 text-slate-400" } };
                  const st = sMap[inv.status] || { l: inv.status, c: "bg-slate-500/10 text-slate-400" };
                  return (
                    <div key={inv.invoice_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 flex items-center gap-4 hover:border-white/10 transition-colors cursor-pointer"
                      onClick={async () => {
                        try {
                          const r = await api.get(`/facturation/documents/${inv.invoice_id}`);
                          setViewingInvoice(r.data);
                          const p = await api.get(`/facturation/documents/${inv.invoice_id}/payments`).catch(() => ({ data: [] }));
                          setInvoicePayments(p.data || []);
                        } catch { setViewingInvoice(inv); }
                      }} data-testid={`client-invoice-${inv.invoice_id}`}>
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${inv.type === "facture" ? "bg-blue-500/10" : inv.type === "devis" ? "bg-green-500/10" : "bg-red-500/10"}`}>
                        <Receipt className={`w-5 h-5 ${inv.type === "facture" ? "text-blue-400" : inv.type === "devis" ? "text-green-400" : "text-red-400"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{inv.type === "facture" ? "Facture" : inv.type === "devis" ? "Devis" : "Avoir"} {inv.reference || ""}</div>
                        <div className="text-xs text-slate-500">{inv.issue_date ? new Date(inv.issue_date).toLocaleDateString("fr-FR") : ""} {inv.client_name ? `- ${inv.client_name}` : ""}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-[#f7c935]">{(inv.total_ttc || 0).toLocaleString("fr")} EUR</div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${st.c}`}>{st.l}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Prestations Tab — Accompagnements + Services unifies */}
        <TabsContent value="prestations">
          <div className="flex flex-wrap gap-2 justify-end mb-4">
            <Button onClick={() => { setSelectedAccTemplate(""); setShowAssignAcc(true); }} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="assign-acc-btn">
              <Plus className="w-4 h-4 mr-2" /> Accompagnement
            </Button>
            <Button onClick={() => setShowAddService(true)} variant="outline" className="border-white/10 text-white hover:bg-white/5 font-bold" data-testid="add-service-btn">
              <Plus className="w-4 h-4 mr-2" /> Service
            </Button>
          </div>

          {accompaniments.length === 0 && services.length === 0 ? (
            <div className="bg-[#14101c] border border-white/10 rounded-xl p-8 text-center text-slate-500">
              Aucune prestation. Creez des modeles dans le <strong>Builder</strong> puis attribuez-les ici.
            </div>
          ) : (
            <div className="space-y-3">
              {/* Accompagnements */}
              {accompaniments.length > 0 && (
                <>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mt-2 mb-1">Accompagnements</div>
                  {accompaniments.map((acc) => {
                    const total = acc.phases?.reduce((s, p) => s + (p.tasks?.length || 0), 0) || 0;
                    const done = acc.phases?.reduce((s, p) => s + (p.tasks?.filter(t => t.status === "done").length || 0), 0) || 0;
                    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                    return (
                      <div key={acc.accompaniment_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5 hover:border-[#f7c935]/30 transition-colors cursor-pointer"
                        onClick={() => navigate(`/admin/accompagnements/${acc.accompaniment_id}`)} data-testid={`prestation-item-${acc.accompaniment_id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <ClipboardCheck className="w-4 h-4 text-purple-400 flex-shrink-0" />
                              <span className="font-semibold">{acc.title}</span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/10 text-purple-400">Accompagnement</span>
                            </div>
                            <div className="text-sm text-slate-500 mt-1">
                              {acc.formula}{acc.duration_months ? ` - ${acc.duration_months} mois` : ""}
                              {acc.price ? ` - ${acc.price} EUR` : ""}
                              {total > 0 ? ` - ${done}/${total} taches` : ""}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${acc.status === "active" ? "bg-green-500/10 text-green-400" : acc.status === "termine" ? "bg-slate-500/10 text-slate-400" : "bg-yellow-500/10 text-yellow-400"}`}>
                              {acc.status === "active" ? "Actif" : acc.status === "termine" ? "Termine" : acc.status || "Actif"}
                            </span>
                            {total > 0 && <span className="text-xl font-black" style={{ color: "#f7c935" }}>{pct}%</span>}
                            <button onClick={(e) => { e.stopPropagation(); deleteAccompagnement(acc.accompaniment_id); }}
                              className="p-1.5 hover:bg-red-500/10 rounded-lg" title="Supprimer" data-testid={`delete-acc-${acc.accompaniment_id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          </div>
                        </div>
                        {total > 0 && (
                          <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
                            <div className="h-full rounded-full bg-[#f7c935] transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}

              {/* Services */}
              {services.length > 0 && (
                <>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mt-4 mb-1">Services</div>
                  {services.map((srv) => {
                    const srvForms = intakeForms.filter(f => f.service_id === srv.service_id);
                    return (
                      <div key={srv.service_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5" data-testid={`prestation-item-${srv.service_id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Briefcase className="w-4 h-4 text-blue-400 flex-shrink-0" />
                              <span className="font-semibold">{srv.name}</span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-400">Service</span>
                            </div>
                            <div className="text-sm text-slate-500 mt-1">
                              {srv.description}{srv.category ? ` - ${srv.category}` : ""}
                              {srv.price ? ` - ${srv.price} EUR` : ""}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${srv.status === "active" ? "bg-green-500/10 text-green-400" : "bg-slate-500/10 text-slate-400"}`}>
                              {srv.status === "active" ? "Actif" : srv.status}
                            </span>
                            <button onClick={(e) => { e.stopPropagation(); handleDeleteService(srv.service_id); }} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                        {srv.intake_form_template_id && (
                          <div className="mt-3 pt-3 border-t border-white/5">
                            <div className="text-xs text-slate-500 mb-1">Document a remplir par le client :</div>
                            {srvForms.length > 0 ? srvForms.map(f => (
                              <div key={f.form_id} className="flex items-center gap-2 text-xs mb-1">
                                <span className="px-2 py-0.5 rounded bg-green-500/10 text-green-400">Rempli</span>
                                <span className="text-slate-400">{f.template_name} - {new Date(f.created_at).toLocaleDateString("fr")}</span>
                                <button onClick={(e) => { e.stopPropagation(); setViewingForm(f); setShowFormView(true); }} className="text-[#f7c935] hover:underline ml-auto">Voir les reponses</button>
                              </div>
                            )) : (
                              <div className="text-xs text-amber-400">En attente - le client doit remplir le document depuis son espace</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </TabsContent>

        {/* Formations Tab */}
        <TabsContent value="formations">
          <div className="mb-4 flex justify-end">
            <Button onClick={() => setShowEnroll(true)} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="enroll-client-btn">
              <GraduationCap className="w-4 h-4 mr-2" /> Attribuer une formation
            </Button>
          </div>
          <div className="space-y-3">
            {enrollments.length === 0 ? (
              <div className="bg-[#14101c] border border-white/10 rounded-xl p-8 text-center text-slate-500">Aucune inscription a une formation</div>
            ) : enrollments.map((enr) => {
              const formation = formations.find(f => f.formation_id === enr.formation_id);
              return (
                <div key={enr.enrollment_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold">{formation?.title || "Formation"}</div>
                      <div className="text-sm text-slate-500">{formation?.code} - {formation?.duration}</div>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full font-medium bg-green-500/10 text-green-400">Inscrit</span>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                    <span className={enr.convention_signed ? "text-green-400" : ""}>Convention {enr.convention_signed ? "signee" : "non signee"}</span>
                    <span className={enr.fiche_filled ? "text-green-400" : ""}>Fiche {enr.fiche_filled ? "remplie" : "a remplir"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* Notes Tab with CRUD */}
        <TabsContent value="notes">
          <div className="flex justify-end mb-4">
            <Button onClick={() => { setEditingNote(null); setNoteForm({ title: "", content: "", category: "general" }); setShowNoteDialog(true); }} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="add-client-note-btn">
              <Plus className="w-4 h-4 mr-2" /> Ajouter une note
            </Button>
          </div>
          <div className="space-y-3">
            {notes.length === 0 ? (
              <div className="bg-[#14101c] border border-white/10 rounded-xl p-8 text-center text-slate-500">Aucune note</div>
            ) : notes.map((note) => (
              <div key={note.note_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5 group">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-semibold">{note.title}</div>
                    <div className="text-sm text-slate-400 mt-1">{note.content}</div>
                    <div className="text-xs text-slate-600 mt-2">{note.author_name} - {new Date(note.created_at).toLocaleDateString("fr-FR")}</div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => openEditNote(note)} className="p-1.5 rounded hover:bg-white/10"><Pencil className="w-3.5 h-3.5 text-slate-400" /></button>
                    <button onClick={() => deleteNote(note.note_id)} className="p-1.5 rounded hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Audits Tab with CRUD */}
        <TabsContent value="audits">
          <div className="flex justify-end mb-4">
            <Button onClick={() => { setEditingAudit(null); setNewAuditForm({ title: "", content: "", status: "en_cours", recommendations: "" }); setShowAuditDialog(true); }} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="add-client-audit-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouvel audit
            </Button>
          </div>
          <div className="space-y-3">
            {audits.length === 0 ? (
              <div className="bg-[#14101c] border border-white/10 rounded-xl p-8 text-center text-slate-500">Aucun audit</div>
            ) : audits.map((audit) => (
              <div key={audit.audit_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5 group">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <div className="font-semibold">{audit.title}</div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${audit.status === "termine" ? "bg-green-500/10 text-green-400" : "bg-blue-500/10 text-blue-400"}`}>
                        {audit.status === "termine" ? "Termine" : "En cours"}
                      </span>
                    </div>
                    <div className="text-sm text-slate-500 mt-1">{audit.content?.substring(0, 200)}</div>
                    {audit.recommendations?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-white/5">
                        <div className="text-xs font-bold text-slate-500 mb-1">Recommandations :</div>
                        <ul className="space-y-1">{audit.recommendations.map((r, j) => (
                          <li key={j} className="text-xs text-slate-500 flex items-start gap-1.5">
                            <span className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{ background: "#f7c935" }} />{r}
                          </li>
                        ))}</ul>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => openEditAudit(audit)} className="p-1.5 rounded hover:bg-white/10"><Pencil className="w-3.5 h-3.5 text-slate-400" /></button>
                    <button onClick={() => deleteAudit(audit.audit_id)} className="p-1.5 rounded hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* CRM & IA Tab — opportunités liées + scoring */}
        <TabsContent value="crm">
          <div className="space-y-4" data-testid="tab-crm-content">
            <div className="flex items-center gap-2 mb-2">
              <Bot className="w-5 h-5 text-[#f7c935]" />
              <div>
                <h3 className="text-base font-bold text-white">Opportunités CRM &amp; profil IA</h3>
                <p className="text-xs text-slate-500">Toutes les opportunités liées à ce client, classées par stage. Cliquez pour ouvrir la fiche complète et déclencher une évaluation IA.</p>
              </div>
            </div>
            <ClientCrmOpportunities api={api} clientId={userId} />
          </div>
        </TabsContent>

        {/* Fiche Tab with AI Summary */}
        <TabsContent value="fiche">
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Button onClick={() => navigate(`/admin/fiche-renseignement/${userId}`)} className="bg-[#461d8a] text-white hover:bg-[#5a2d9c] font-bold" data-testid="view-fiche-btn">
                <ClipboardList className="w-4 h-4 mr-2" /> Voir / Modifier la fiche
              </Button>
              <label className="cursor-pointer">
                <input type="file" className="hidden" accept=".docx,.pdf" onChange={handleImportFiche} />
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg bg-[#14101c] border border-white/10 text-white font-bold text-sm hover:bg-white/5 transition-colors ${importingFiche ? "opacity-50 pointer-events-none" : ""}`} data-testid="import-fiche-btn">
                  {importingFiche ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  <span>{importingFiche ? "Analyse IA..." : "Importer DOCX/PDF"}</span>
                </div>
              </label>
              <Button onClick={handleGenerateAI} disabled={generatingAI} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold ml-auto" data-testid="ai-summary-btn">
                {generatingAI ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Brain className="w-4 h-4 mr-2" />}
                {generatingAI ? "Analyse en cours..." : "Resume IA"}
              </Button>
            </div>

            {/* Quick fiche preview */}
            {Object.keys(fiche).length > 2 && (
              <div className="bg-[#14101c] border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-bold mb-4" style={{ color: "#f7c935" }}>Apercu de la fiche</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {[
                    { label: "Raison sociale", key: "raison_sociale" },
                    { label: "Forme juridique", key: "forme_juridique" },
                    { label: "SIRET", key: "siret" },
                    { label: "Secteur", key: "secteur" },
                    { label: "Effectif", key: "effectif" },
                    { label: "CA N-1", key: "ca_n1" },
                    { label: "Evolution", key: "evolution_ca" },
                    { label: "Tresorerie", key: "tresorerie" },
                    { label: "Situation", key: "situation_actuelle" },
                    { label: "Budget", key: "budget_mensuel" },
                    { label: "Urgence", key: "urgence" },
                    { label: "Volonte changement", key: "volonte_changement" },
                  ].filter(({ key }) => fiche[key]).map(({ label, key }) => (
                    <div key={key} className="py-2 border-b border-white/5">
                      <div className="text-xs text-slate-500">{label}</div>
                      <div className="text-sm font-medium">{fiche[key]}</div>
                    </div>
                  ))}
                </div>
                {fiche.difficultes?.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-white/5">
                    <div className="text-xs text-slate-500 mb-2">Difficultes identifiees</div>
                    <div className="flex flex-wrap gap-1.5">
                      {fiche.difficultes.map((d, i) => (
                        <span key={i} className="text-xs px-2 py-1 rounded-full bg-red-500/10 text-red-400">{d}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* AI Summary */}
            {aiSummary && (
              <div className="bg-[#14101c] border border-[#461d8a]/30 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Brain className="w-5 h-5" style={{ color: "#f7c935" }} />
                  <h3 className="text-lg font-bold">Resume IA</h3>
                  {fiche.ai_summary_date && <span className="text-xs text-slate-600 ml-auto">{new Date(fiche.ai_summary_date).toLocaleDateString("fr-FR")}</span>}
                </div>
                <div className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{aiSummary}</div>
              </div>
            )}

            {!aiSummary && Object.keys(fiche).length < 3 && (
              <div className="bg-[#14101c] border border-white/10 rounded-xl p-8 text-center text-slate-500">
                <ClipboardList className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p>Aucune fiche de renseignement remplie.</p>
                <p className="text-xs mt-1">Le client doit remplir sa fiche ou vous pouvez la creer via le bouton ci-dessus.</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Equipe Tab */}
        <TabsContent value="equipe">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setShowAssignStaff(true)} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="assign-staff-to-client-btn">
              <UsersRound className="w-4 h-4 mr-2" /> Attribuer un membre
            </Button>
          </div>
          <div className="space-y-3">
            {assignments.length === 0 ? (
              <div className="bg-[#14101c] border border-white/10 rounded-xl p-8 text-center text-slate-500">Aucun coach ou admin attribue</div>
            ) : assignments.map(asgn => (
              <div key={asgn.assignment_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5 flex items-center justify-between group">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                    style={{ background: "#461d8a", color: asgn.staff_role === "coach" ? "#3b82f6" : asgn.staff_role === "sous_traitant" ? "#10b981" : "#f7c935" }}>
                    {asgn.staff_name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div>
                    <div className="font-semibold">{asgn.staff_name || "Sans nom"}</div>
                    <div className="text-sm text-slate-500">{asgn.staff_email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-1 rounded-full font-medium"
                    style={{ background: `${asgn.staff_role === "coach" ? "#3b82f6" : asgn.staff_role === "sous_traitant" ? "#10b981" : "#f7c935"}15`, color: asgn.staff_role === "coach" ? "#3b82f6" : asgn.staff_role === "sous_traitant" ? "#10b981" : "#f7c935" }}>
                    {asgn.staff_role === "coach" ? "Coach" : asgn.staff_role === "sous_traitant" ? "Sous-traitant" : "Admin"}
                  </span>
                  <button onClick={() => handleRemoveAssignment(asgn.assignment_id)} className="p-1.5 rounded hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Documents Tab with upload */}
        <TabsContent value="documents">
          <div className="flex justify-end mb-4">
            <label className="cursor-pointer">
              <input type="file" className="hidden" onChange={handleDocUpload} />
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#f7c935] text-[#14101c] font-bold text-sm hover:bg-[#e6b825] transition-colors" data-testid="upload-client-doc-btn">
                {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
                Upload un document
              </div>
            </label>
          </div>
          <div className="space-y-3">
            {documents.length === 0 ? (
              <div className="bg-[#14101c] border border-white/10 rounded-xl p-8 text-center text-slate-500">Aucun document</div>
            ) : documents.map((doc) => (
              <div key={doc.document_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{doc.title}</div>
                    <div className="text-sm text-slate-500">{doc.doc_type} - {new Date(doc.created_at).toLocaleDateString("fr-FR")}</div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${doc.status === "soumis" ? "bg-green-500/10 text-green-400" : "bg-yellow-500/10 text-yellow-400"}`}>
                    {doc.status === "soumis" ? "Soumis" : "Brouillon"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Timeline/Parcours Tab */}
        <TabsContent value="timeline">
          <ClientTimelineTab clientTimeline={clientTimeline} timelineFilter={timelineFilter} setTimelineFilter={setTimelineFilter} />
        </TabsContent>

        {/* Acces Tab - Restructured */}
        <TabsContent value="acces">
          <ClientAccessTab
            clientAccess={clientAccess} setClientAccess={setClientAccess} toggleAccess={toggleAccess}
            saveClientAccess={saveClientAccess} savingAccess={savingAccess}
            data={data} handleToggleBlock={handleToggleBlock} blockingAccount={blockingAccount}
            showResetPw={showResetPw} setShowResetPw={setShowResetPw}
            newPassword={newPassword} setNewPassword={setNewPassword}
            handleResetPassword={handleResetPassword} resettingPw={resettingPw}
            setShowDeleteConfirm={setShowDeleteConfirm}
          />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <NoteDialog open={showNoteDialog} onOpenChange={setShowNoteDialog} editingNote={editingNote} noteForm={noteForm} setNoteForm={setNoteForm} saveNote={saveNote} />
      <AuditDialog open={showAuditDialog} onOpenChange={setShowAuditDialog} editingAudit={editingAudit} auditForm={newAuditForm} setAuditForm={setNewAuditForm} saveAudit={saveAudit} />
      <EnrollDialog open={showEnroll} onOpenChange={setShowEnroll} formations={availableFormations} enrollFormationId={enrollFormationId} setEnrollFormationId={setEnrollFormationId} handleEnroll={handleEnroll} />
      <DeleteConfirmDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm} userName={user?.name || user?.email} handleDeleteClient={handleDeleteClient} deleting={deleting} />
      <AssignStaffDialog open={showAssignStaff} onOpenChange={setShowAssignStaff} allStaff={allStaff} assignments={assignments} assignStaffId={assignStaffId} setAssignStaffId={setAssignStaffId} handleAssignStaff={handleAssignStaff} />
      <AddServiceDialog open={showAddService} onOpenChange={setShowAddService} catalogServices={catalogServices} docTemplates={docTemplates} selectedCatalog={selectedCatalog} setSelectedCatalog={setSelectedCatalog} selectedIntakeTemplate={selectedIntakeTemplate} setSelectedIntakeTemplate={setSelectedIntakeTemplate} handleAddServiceFromCatalog={handleAddServiceFromCatalog} />
      <FormViewDialog open={showFormView} onOpenChange={setShowFormView} viewingForm={viewingForm} />
      <AssignAccDialog open={showAssignAcc} onOpenChange={setShowAssignAcc} accTemplates={accTemplates} selectedAccTemplate={selectedAccTemplate} setSelectedAccTemplate={setSelectedAccTemplate} handleAssignAccompaniment={handleAssignAccompaniment} />
      <TagsDialog open={showTags} onOpenChange={setShowTags} tags={tags} />
      <InvoiceDetailDialog viewingInvoice={viewingInvoice} setViewingInvoice={setViewingInvoice} invoicePayments={invoicePayments} api={api} />
    </div>
  );
}
